import { UcoastVideo } from '@/scripts/core/ucoast-video'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { type Hls } from '@/scripts/global'

export class MediaManager {
	hlsRequired: boolean
	hlsLibraryLoaded: boolean
	initialLoadStarted: boolean
	Hls?: Hls
	playCallbacks: (() => void)[]
	hlsLibIsSupported = false
	videos: UcoastVideo[]
	videoObserver: IntersectionObserver
	private hlsLoadPromise?: Promise<void>

	constructor() {
		this.hlsLibraryLoaded = false
		this.initialLoadStarted = false
		this.playCallbacks = []
		this.hlsRequired = this.isHlsRequired()
		this.videos = []
		this.videoObserver = new IntersectionObserver(
			this.onIntersection.bind(this),
			{
				rootMargin: '0px 0px -50px 0px',
			}
		)
	}

	// public methods
	async initialLoad() {
		if (this.initialLoadStarted) return
		this.initialLoadStarted = true

		const runInitialLoad = () => {
			const imageFadeTargets: {
				img: HTMLImageElement
				fadeTarget: HTMLElement
			}[] = []
			document
				.querySelectorAll('picture, img.no-picture')
				.forEach((element: Element) => {
					if (element instanceof HTMLPictureElement) {
						const img = q.os<HTMLImageElement>('img', element)
						if (img) {
							imageFadeTargets.push({
								img,
								fadeTarget: element,
							})
						}
					} else if (
						element instanceof HTMLImageElement &&
						element.classList.contains('no-picture')
					) {
						imageFadeTargets.push({
							img: element,
							fadeTarget: element,
						})
					}
				})

			const skipFadeIns = imageFadeTargets.map(({ img, fadeTarget }) =>
				shouldSkipFadeIn(img, fadeTarget)
			)
			imageFadeTargets.forEach(({ img, fadeTarget }, index) => {
				handleImageFadeIn(img, fadeTarget, skipFadeIns[index])
			})

			this.loadEarlyVideos()
		}

		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', runInitialLoad, {
				once: true,
			})
			return
		}

		runInitialLoad()
	}

	loadEarlyVideos() {
		const videos = q.ol<UcoastVideo>('ucoast-video[data-retry="true"]')
		if (!videos) return
		videos.forEach((video) => {
			if (this.videos.some((item) => video === item)) return
			video.unMarkForRetry()
			void video.onConnectedCallback()
		})
	}

	onIntersection(elements: IntersectionObserverEntry[]) {
		elements.forEach((element) => {
			if (!isVideoComponent(element.target)) return
			if (element.isIntersecting) {
				const elementTarget: UcoastVideo = element.target
				void elementTarget.play()
			} else {
				const elementTarget: UcoastVideo = element.target
				elementTarget.pause()
			}
		})
	}

	addVideo(video: UcoastVideo) {
		if (!this.videos.some((item) => video === item)) {
			this.videoObserver.observe(video)
			this.videos.push(video)
		}
	}
	removeVideo(video: UcoastVideo) {
		this.videoObserver.unobserve(video)
		this.videos = this.videos.filter((item) => video !== item)
	}

	// hls

	private isHlsRequired() {
		const video = document.createElement('video')
		return video.canPlayType('application/vnd.apple.mpegurl') === ''
	}

	async ensureHlsFor(video?: UcoastVideo) {
		if (!this.hlsRequired) return
		if (this.hlsLibraryLoaded) {
			if (video) await video.onLibraryLoad()
			return
		}
		this.hlsLoadPromise ??= this.loadHls()
		await this.hlsLoadPromise
		if (!this.hlsLibraryLoaded) {
			video?.useMp4Fallback()
			return
		}
		if (video) await video.onLibraryLoad()
	}

	private async loadHls() {
		try {
			// ignoring because types are defined through global
			// @ts-ignore
			const hlsLib: Hls = await import('hls.js/dist/hls.light.min.js')
			this.hlsLibIsSupported = hlsLib.isSupported()
			this.Hls = hlsLib
			this.hlsLibraryLoaded = true
		} catch (error) {
			this.hlsLoadPromise = undefined
			console.error('Failed to load the HLS library', error)
		}
	}

	async playAllInContainer(container: HTMLElement) {
		const videosInContainer = q.ol<UcoastVideo>('ucoast-video', container)
		if (!videosInContainer) return
		videosInContainer.forEach((video) => {
			void video.playEventOn()
		})
	}

	async pauseAllInContainer(container: HTMLElement) {
		const videosInContainer = q.ol<UcoastVideo>('ucoast-video', container)
		if (!videosInContainer) return
		videosInContainer.forEach((video) => {
			void video.playEventOff()
		})
	}
}

export function isVideoComponent(
	obj: HTMLElement | Element
): obj is UcoastVideo {
	if (!obj) return false
	if (obj.localName !== 'ucoast-video') return false
	return true
}

function handleImageFadeIn(
	img: HTMLImageElement,
	fadeTarget: HTMLElement,
	skipFadeIn: boolean
): void {
	const fadeDuration = 300

	if (skipFadeIn) {
		fadeTarget.style.opacity = '1'
		fadeTarget.style.removeProperty('transition')
		return
	}

	fadeTarget.style.opacity = '0'
	fadeTarget.style.transition = `opacity ${fadeDuration}ms ease-in-out`

	const reveal = () => {
		requestAnimationFrame(() => {
			fadeTarget.style.opacity = '1'
		})
	}

	if (img.complete && img.naturalWidth > 0) {
		img.decode().then(reveal).catch(reveal)
		return
	}

	img.addEventListener(
		'load',
		() => {
			img.decode().then(reveal).catch(reveal)
		},
		{ once: true }
	)
	img.addEventListener('error', reveal, { once: true })
}

function shouldSkipFadeIn(
	img: HTMLImageElement,
	fadeTarget: HTMLElement
): boolean {
	if (img.complete && img.naturalWidth > 0) return true
	if (img.loading === 'eager') return true
	if (img.getAttribute('fetchpriority') === 'high') return true
	if (img.closest('.header__heading-logo-media')) return true

	const rect = fadeTarget.getBoundingClientRect()
	return (
		rect.width > 0 &&
		rect.height > 0 &&
		rect.bottom > 0 &&
		rect.right > 0 &&
		rect.top < window.innerHeight &&
		rect.left < window.innerWidth
	)
}
